import { getFragmentData } from 'gql'
import { ErrorFragmentFragmentDoc } from 'gql/graphql'
import { enqueueSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useAbility } from 'providers/casl'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { toDTO } from './api/mapper'
import { UPDATE_DOMAIN_INTERSTITIAL } from './api/mutation'

import type { UseUpdateDomainInterstitial } from './types'
import type { Schema } from 'features/inventory/domain/forms/RichPlacementsForm/sections/InterstitialForm/schema'
import type {
  UpdateDomainInterstitialMutation,
  UpdateDomainInterstitialMutationVariables,
} from 'gql/graphql'
import type { FieldErrors } from 'react-hook-form'

export const useUpdateDomainInterstitial = (): UseUpdateDomainInterstitial => {
  const ability = useAbility()
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.richPlacements.interstitial.action',
  })
  const { domain } = useContext(AddEditDomainContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  if (domain === undefined) {
    throw Error('Domain should be accessible')
  }

  const { isPending, mutateAsync } = useMutation<
    { input: UpdateDomainInterstitialMutationVariables['input'] },
    UpdateDomainInterstitialMutation
  >({
    mutation: UPDATE_DOMAIN_INTERSTITIAL,
    onCompleted: async (data: UpdateDomainInterstitialMutation): Promise<void> => {
      const apiErrors = data.updateDomainRichPlacementInterstitialConfiguration?.errors

      if (apiErrors && apiErrors.length > 0) {
        setErrors(mapGraphQLErrors(getFragmentData(ErrorFragmentFragmentDoc, apiErrors)))
        enqueueSnackbar(t('error'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('success'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['domain'],
  })

  const update = (data: Schema): void =>
    void mutateAsync({
      input: {
        ...toDTO(data, ability),
        domainId: domain.id,
      },
    })

  return { errors, isPending, update }
}
